import React, { useEffect, useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { Button, InlineConfirm } from '@duck/ui'
import { toast } from 'sonner'
import { useTar } from '@/modules'
import { apis } from '@/modules/apiClients'
import { StandardLoader } from '@/components/StandardLoader'
import {
  ArrowPathIcon,
  ArrowUpOnSquareIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

interface ChallengeActionsProps {
  challengeInfo: ChallengeService.ChallengeInfo
  onChanged: () => void
  onDeleted: () => void
}

export const ChallengeActions: React.FC<ChallengeActionsProps> = ({
  challengeInfo,
  onChanged,
  onDeleted,
}) => {
  const { t } = useTar()
  const { challenge } = challengeInfo
  const challengeId = challenge.id

  // Optimistic release state (mirrors ChallengesTable pattern)
  const [releaseOverride, setReleaseOverride] = useState<boolean | undefined>(undefined)
  const [releasePending, setReleasePending] = useState(false)

  // PDF download
  const [pdfDownloading, setPdfDownloading] = useState(false)

  // Resubmit — two-click confirm (inline button label flip)
  const [resubmitConfirm, setResubmitConfirm] = useState(false)
  const [resubmitPending, setResubmitPending] = useState(false)

  // Delete — InlineConfirm widget
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deletePending, setDeletePending] = useState(false)

  const effectiveRelease = releaseOverride ?? Boolean(challenge.feedbackReleaseOverride)

  // Reset two-click resubmit confirm when navigating to a different challenge
  useEffect(() => {
    setResubmitConfirm(false)
  }, [challengeId])

  const isFinished = challenge.status === ChallengeService.ChallengeStatusEnum.Finished
  const isProcessed = challenge.feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed

  const handleToggleFeedbackRelease = async () => {
    if (releasePending) return
    const newValue = !effectiveRelease
    setReleasePending(true)
    setReleaseOverride(newValue)
    try {
      await apis.challengeService.releaseFeedback({ challengeId, release: newValue })
      onChanged()
      setReleaseOverride(undefined)
    } catch (error) {
      console.error('Failed to update feedback release override', { challengeId, release: newValue, error })
      setReleaseOverride(!newValue)
      toast.error(t('common.anErrorOccurred'))
    } finally {
      setReleasePending(false)
    }
  }

  const handleDownloadFeedbackPdf = async () => {
    if (pdfDownloading) return
    setPdfDownloading(true)
    try {
      const response = await apis.challengeService.getFeedbackPdf(challengeId, {
        responseType: 'blob',
      })
      const blob = response.data as Blob
      const contentDisposition = response.headers['content-disposition'] as string | undefined
      let fileName: string | undefined
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
        if (filenameStarMatch?.[1]) {
          try {
            fileName = decodeURIComponent(filenameStarMatch[1])
          } catch {
            // fall back to basic filename
          }
        }
        if (!fileName) {
          const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/)
          fileName = fileNameMatch?.[1]
        }
      }
      fileName = fileName ?? `feedback_${challengeId}.pdf`
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('challenges.failedToDownloadPdf'))
    } finally {
      setPdfDownloading(false)
    }
  }

  const handleResubmitClick = async () => {
    if (resubmitPending) return
    if (!resubmitConfirm) {
      setResubmitConfirm(true)
      return
    }
    setResubmitConfirm(false)
    setResubmitPending(true)
    try {
      await apis.challengeService.resubmitChallenge({ challengeId })
      toast.success(t('challenges.resubmitSuccess'))
      onChanged()
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error as Error)?.message ??
        'Unknown error'
      console.error('Failed to resubmit challenge', { challengeId, error })
      toast.error(`${t('challenges.resubmitFailed')}: ${message}`)
    } finally {
      setResubmitPending(false)
    }
  }

  const handleDelete = async () => {
    if (deletePending) return
    setDeletePending(true)
    try {
      await apis.challengeService.deleteChallenge(challengeId)
      onDeleted()
    } catch (error) {
      console.error('Failed to delete challenge', { challengeId, error })
      toast.error(t('common.anErrorOccurred'))
      setDeleteConfirmOpen(false)
    } finally {
      setDeletePending(false)
    }
  }

  if (deleteConfirmOpen) {
    return (
      <InlineConfirm
        variant="danger"
        message={t('common.areYouSure')}
        onConfirm={() => { void handleDelete() }}
        onCancel={() => setDeleteConfirmOpen(false)}
        confirmDisabled={deletePending}
        className="max-w-sm"
      />
    )
  }

  return (
    <div className="flex flex-wrap gap-2 justify-end">
      {/* Release / Unrelease */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => { void handleToggleFeedbackRelease() }}
        disabled={releasePending}
      >
        <ArrowUpOnSquareIcon className="h-4 w-4 me-1.5" />
        {effectiveRelease ? t('challenges.unreleaseFeedback') : t('challenges.releaseFeedback')}
      </Button>

      {/* Download PDF — only when processed */}
      {isProcessed && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => { void handleDownloadFeedbackPdf() }}
          disabled={pdfDownloading}
        >
          {pdfDownloading ? (
            <>
              <StandardLoader size="sm" inline className="!m-0 me-1.5" />
              {t('common.generating')}
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="h-4 w-4 me-1.5" />
              {t('challenges.downloadFeedbackPdf')}
            </>
          )}
        </Button>
      )}

      {/* Resubmit — only when Finished */}
      {isFinished && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => { void handleResubmitClick() }}
          disabled={resubmitPending}
        >
          <ArrowPathIcon className="h-4 w-4 me-1.5" />
          {resubmitConfirm ? t('common.areYouSure') : t('challenges.resubmit')}
        </Button>
      )}

      {/* Delete */}
      <Button
        variant="destructive"
        size="sm"
        onClick={() => setDeleteConfirmOpen(true)}
      >
        <TrashIcon className="h-4 w-4 me-1.5" />
        {t('common.delete')}
      </Button>
    </div>
  )
}
